import React, { Component } from "react";
import DatePicker from "react-datepicker";
import * as moment from "moment";
import "moment-timezone";
import { Row, Col } from "react-bootstrap";

import HistoricalDayChart from "../components/charts/HistoricalDayChart";
import RangeChart from "../components/charts/RangeChart";

const BASE_API_ENDPOINT = "/battery-datacenter-api/api/v1";

const METRIC_OPTIONS = ["energyIn", "energyOut", "energyTotal"];

const ALL_SENSORS = (
  process.env.REACT_APP_INITIAL_ACTIVE_CHARTS || "b1v,b1a"
).split(",");

function extractDayInfo(item) {
  if (!item.date) return null;
  const [year, month, day] = item.date.split("-").map(Number);
  return { year, month, day };
}

function formatKWh(value) {
  return value.toFixed(4) + " kWh";
}

function isKWhKey(key) {
  return key.toLowerCase().includes("kwh");
}

class BalanceDetails extends Component {
  constructor(props) {
    super(props);

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const startDate = new Date(yesterday);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(yesterday);
    endDate.setHours(23, 59, 0, 0);

    const now = new Date();

    this.state = {
      // Detailed balance
      startDate,
      endDate,
      detailedData: null,
      detailedLoading: false,

      // Max days finder
      metric: "energyIn",
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      maxDaysData: null,
      maxDaysLoading: false,

      // Selected day for chart view (stores the raw item from the response)
      selectedDayItem: null,

      // Toggle charts for the detailed balance date range
      showDetailedCharts: false,

      // Which sensors to show (shared by both chart sections)
      selectedSensors: ["b1v", "b1a"]
    };
  }

  convertFromBrowserTimezone(ms) {
    const localMoment = moment(ms);
    const targetMoment = moment.tz(
      localMoment.format("YYYY-MM-DDTHH:mm:ss"),
      "YYYY-MM-DDTHH:mm:ss",
      process.env.REACT_APP_PROJECT_TIMEZONE
    );
    return targetMoment.format("x");
  }

  async fetchDetailedBalance() {
    const { startDate, endDate } = this.state;
    const fromTs = this.convertFromBrowserTimezone(startDate.getTime());
    const toTs = this.convertFromBrowserTimezone(endDate.getTime());

    if (parseInt(fromTs) > parseInt(toTs)) {
      alert("Start date must be before end date.");
      return;
    }

    this.setState({ detailedLoading: true, detailedData: null });

    try {
      const url = `${BASE_API_ENDPOINT}/reading/energeticbalance/detailed/between/${fromTs}/${toTs}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        this.setState({ detailedData: data, detailedLoading: false });
      } else {
        this.setState({ detailedLoading: false });
        alert(`Server error ${res.status}: could not fetch detailed balance.`);
      }
    } catch (e) {
      this.setState({ detailedLoading: false });
      alert(`Network error: ${e}`);
    }
  }

  async fetchMaxDays() {
    const { metric, year, month } = this.state;
    this.setState({
      maxDaysLoading: true,
      maxDaysData: null,
      selectedDayItem: null
    });

    try {
      const url = `${BASE_API_ENDPOINT}/reading/energeticbalance/max/day?metric=${metric}&year=${year}&month=${month}&limit=5`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        this.setState({ maxDaysData: data, maxDaysLoading: false });
      } else {
        this.setState({ maxDaysLoading: false });
        alert(`Server error ${res.status}: could not fetch max days.`);
      }
    } catch (e) {
      this.setState({ maxDaysLoading: false });
      alert(`Network error: ${e}`);
    }
  }

  toggleDayCharts(item) {
    this.setState(prev => ({
      selectedDayItem: prev.selectedDayItem === item ? null : item
    }));
  }

  renderDetailedData(data) {
    const items = Array.isArray(data) ? data : [data];
    if (!items.length) return <p>No data returned.</p>;

    const keys = Object.keys(items[0]);

    return (
      <table className="balance-details-table">
        <thead>
          <tr>
            {keys.map(k => (
              <th key={k}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i}>
              {keys.map(k => (
                <td key={k}>
                  {typeof row[k] === "number" && isKWhKey(k)
                    ? formatKWh(row[k])
                    : String(row[k] != null ? row[k] : "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  renderMaxDaysTable(maxDaysData) {
    if (!maxDaysData || !maxDaysData.length) return null;
    const { selectedDayItem } = this.state;
    const keys = Object.keys(maxDaysData[0]);

    return (
      <div style={{ overflowX: "auto", marginTop: 20 }}>
        <table className="balance-details-table">
          <thead>
            <tr>
              <th>#</th>
              {keys.map(k => (
                <th key={k}>{k}</th>
              ))}
              <th>Charts</th>
            </tr>
          </thead>
          <tbody>
            {maxDaysData.map((item, i) => {
              const dayInfo = extractDayInfo(item);
              const isSelected = selectedDayItem === item;

              return (
                <tr
                  key={i}
                  className={isSelected ? "balance-details-selected-row" : ""}
                >
                  <td>{i + 1}</td>
                  {keys.map(k => (
                    <td key={k}>
                      {typeof item[k] === "number" && isKWhKey(k)
                        ? formatKWh(item[k])
                        : String(item[k] != null ? item[k] : "")}
                    </td>
                  ))}
                  <td>
                    {dayInfo ? (
                      <button
                        className={`view-charts-btn${isSelected ? " active" : ""}`}
                        onClick={() => this.toggleDayCharts(item)}
                      >
                        {isSelected ? "Hide Charts" : "View Charts"}
                      </button>
                    ) : (
                      <span className="balance-details-no-date">
                        date unknown
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderDayCharts(selectedDayItem) {
    const { selectedSensors } = this.state;
    const dayInfo = extractDayInfo(selectedDayItem);
    if (!dayInfo) return null;

    const dateStr = `${dayInfo.year}-${String(dayInfo.month).padStart(2, "0")}-${String(dayInfo.day).padStart(2, "0")}`;

    if (selectedSensors.length === 0) {
      return (
        <div style={{ width: "100%" }}>
          <hr />
          <h3>Sensor Charts — <span className="balance-details-date-label">{dateStr}</span></h3>
          <p style={{ color: "gray" }}>No sensors selected. Check at least one sensor above.</p>
        </div>
      );
    }

    // Pair sensors side-by-side (mirrors home-page layout)
    const rows = [];
    for (let i = 0; i < selectedSensors.length; i += 2) {
      rows.push(selectedSensors.slice(i, i + 2));
    }

    return (
      <div style={{ width: "100%" }}>
        <hr />
        <h3>
          Sensor Charts —{" "}
          <span className="balance-details-date-label">{dateStr}</span>
        </h3>
        {rows.map((pair, idx) => (
          <Row key={idx}>
            {pair.map(sensor => (
              <Col xs={12} lg={6} key={sensor}>
                <HistoricalDayChart
                  sensorName={sensor}
                  year={dayInfo.year}
                  month={dayInfo.month}
                  day={dayInfo.day}
                />
              </Col>
            ))}
          </Row>
        ))}
      </div>
    );
  }

  renderDetailedCharts() {
    const { startDate, endDate, selectedSensors } = this.state;

    const startTs = parseInt(this.convertFromBrowserTimezone(startDate.getTime()), 10);
    const endTs = parseInt(this.convertFromBrowserTimezone(endDate.getTime()), 10);

    const fromStr = moment(startDate).format("YYYY-MM-DD HH:mm");
    const toStr = moment(endDate).format("YYYY-MM-DD HH:mm");

    if (selectedSensors.length === 0) {
      return (
        <div style={{ width: "100%", marginTop: 16 }}>
          <p style={{ color: "gray" }}>
            No sensors selected. Check at least one sensor in "Charts to show:" below.
          </p>
        </div>
      );
    }

    const rows = [];
    for (let i = 0; i < selectedSensors.length; i += 2) {
      rows.push(selectedSensors.slice(i, i + 2));
    }

    return (
      <div style={{ width: "100%", marginTop: 16 }}>
        <h4>
          Sensor Charts —{" "}
          <span className="balance-details-date-label">{fromStr}</span>
          {" "}&rarr;{" "}
          <span className="balance-details-date-label">{toStr}</span>
        </h4>
        {rows.map((pair, idx) => (
          <Row key={idx}>
            {pair.map(sensor => (
              <Col xs={12} lg={6} key={sensor}>
                <RangeChart
                  sensorName={sensor}
                  startTs={startTs}
                  endTs={endTs}
                />
              </Col>
            ))}
          </Row>
        ))}
      </div>
    );
  }

  render() {
    const {
      startDate,
      endDate,
      detailedData,
      detailedLoading,
      metric,
      year,
      month,
      maxDaysData,
      maxDaysLoading,
      selectedDayItem,
      showDetailedCharts,
      selectedSensors
    } = this.state;

    const monthNames = moment.months();

    return (
      <div className="page-content about">
        {/* ── Section 1: Detailed balance ── */}
        <div style={{ width: "100%" }}>
          <h3>Detailed Energy Balance</h3>

          <div className="grid-container">
            <div className="grid-item">
              From:
              <br />
              <div className="datePicker-balance-page">
                <DatePicker
                  selected={startDate}
                  onChange={d => this.setState({ startDate: d })}
                  showTimeSelect
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="From"
                  dateFormat="yyyy/MM/dd HH:mm"
                  maxDate={endDate}
                />
              </div>
            </div>

            <div className="grid-item">
              To:
              <br />
              <div className="datePicker-balance-page">
                <DatePicker
                  selected={endDate}
                  onChange={d => this.setState({ endDate: d })}
                  showTimeSelect
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="To"
                  dateFormat="yyyy/MM/dd HH:mm"
                  minDate={startDate}
                  maxDate={new Date()}
                />
              </div>
            </div>

            <div className="grid-item full-width">
              <button
                onClick={() => this.fetchDetailedBalance()}
                disabled={detailedLoading}
              >
                {detailedLoading ? "Calculating…" : "Calculate Detailed Balance"}
              </button>
              &nbsp;&nbsp;
              <button
                className={`view-charts-btn${showDetailedCharts ? " active" : ""}`}
                onClick={() => this.setState(prev => ({ showDetailedCharts: !prev.showDetailedCharts }))}
              >
                {showDetailedCharts ? "Hide Charts" : "View Charts"}
              </button>

              {detailedLoading && <div className="loader" />}
            </div>
          </div>

          {detailedData && (
            <div
              className="energyData"
              style={{ fontSize: "1rem", overflowX: "auto", marginTop: 12 }}
            >
              {this.renderDetailedData(detailedData)}
            </div>
          )}

          {showDetailedCharts && this.renderDetailedCharts()}
        </div>

        <hr />

        {/* ── Shared sensor picker ── */}
        <div className="balance-details-sensor-picker">
          <span className="balance-details-sensor-picker-label">Charts to show:</span>
          {ALL_SENSORS.map(sensor => (
            <label key={sensor} className="balance-details-sensor-checkbox">
              <input
                type="checkbox"
                checked={selectedSensors.includes(sensor)}
                onChange={e => {
                  const next = e.target.checked
                    ? [...selectedSensors, sensor]
                    : selectedSensors.filter(s => s !== sensor);
                  this.setState({ selectedSensors: next });
                }}
              />
              {sensor}
            </label>
          ))}
        </div>

        <hr />

        {/* ── Section 2: Max days by energy metric ── */}
        <div style={{ width: "100%" }}>
          <h3>Top 5 Days by Energy Metric</h3>

          <div className="balance-details-controls">
            <label>
              Metric:&nbsp;
              <select
                value={metric}
                onChange={e => this.setState({ metric: e.target.value })}
              >
                {METRIC_OPTIONS.map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Year:&nbsp;
              <input
                type="number"
                value={year}
                min="2018"
                max={new Date().getFullYear()}
                onChange={e =>
                  this.setState({ year: parseInt(e.target.value) || year })
                }
                style={{ width: 80 }}
              />
            </label>

            <label>
              Month:&nbsp;
              <select
                value={month}
                onChange={e =>
                  this.setState({ month: parseInt(e.target.value) })
                }
              >
                {monthNames.map((name, i) => (
                  <option key={i + 1} value={i + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <button
              onClick={() => this.fetchMaxDays()}
              disabled={maxDaysLoading}
            >
              {maxDaysLoading ? "Loading…" : "Find Max Days"}
            </button>
          </div>

          {maxDaysLoading && <div className="loader" />}

          {maxDaysData !== null && maxDaysData.length === 0 && (
            <p style={{ marginTop: 20, color: "gray" }}>
              No data found for the selected period.
            </p>
          )}

          {maxDaysData !== null &&
            maxDaysData.length > 0 &&
            this.renderMaxDaysTable(maxDaysData)}
        </div>

        {/* ── Section 3: Charts for the selected day ── */}
        {selectedDayItem &&
          this.renderDayCharts(selectedDayItem)}
      </div>
    );
  }
}

export default BalanceDetails;
